import { gql } from "@apollo/client";
import { Search, SEO } from "@components";
import { FaustTemplate, useFaustQuery } from "@faustwp/core";
import { GetPageCategoriesQuery, GetSearchPageQuery } from "@graphqlTypes";
import { useEffect } from "react";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { HIGHLIGHT_COLORS } from "@/core/constants";
import { PageDataFragment } from "@/fragments/PageData";
import { getBasicQueryVars, getPageTitle, getUrlLocale } from "@/core/utils";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

const SearchPage: FaustTemplate<any> = ({ loading, pageWrapperData }: FaustPageProps<any>) => {
  const pageData = useFaustQuery<GetSearchPageQuery>(PageQuery);
  const categoryData = useFaustQuery<GetPageCategoriesQuery>(PageCategoryQuery);
  const { setHighlightColor, highlightColor } = usePageHighlight();
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const {  siteSettings } = usePageWrapperData();
  const { pressReleaseSearchImage, siteTitle } = siteSettings || {};
  
  const { title, searchPageData, databaseId, pageData: { ogTitle, ogDescription, featuredImage, pageCategory } = {} } = pageData?.rhPage || {};
  const { pageCategories } = categoryData;
  const [category] = pageCategory || [];
  
  useEffect(() => {
    if (!highlightColor) setHighlightColor(HIGHLIGHT_COLORS.GREEN);
  }, [pageCategory]);

  useTrackRouteChange(category?.slug);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  const seoTitle = getPageTitle(siteTitle, ogTitle || title);
  
  if (loading) return;

  return (
    <>
      <SEO title={seoTitle} description={ogDescription} image={featuredImage} />
      <Search
        page={searchPageData}
        pressReleaseSearchImage={pressReleaseSearchImage}
        databaseId={databaseId}
        categories={pageCategories.nodes}
      />
    </>
  );
}

const PageQuery = gql`
  ${PageDataFragment}
  query GetSearchPage($databaseId: ID!, $asPreview: Boolean = false, $language: String = "en") {
    rhPage(id: $databaseId, asPreview: $asPreview, language: $language) {
      title
      databaseId
      ...PageData
      searchPageData {
        searchPlaceholder
        resultsText
        noResults {
          headline
          body
          cta {
            linkType
            externalLink
            label
            page {
              node {
                uri
              }
            }
          }
        }
        noShopResults {
          headline
          body
          cta {
            linkType
            externalLink
            label
            page {
              node {
                uri
              }
            }
          }
        }
        rockHallLabel
        shopLabel
        brandFilterLabel
        shopCarousel {
          headline
          ctaLabel
        }
      }
    }
  }
`;

const PageCategoryQuery = gql`
  query GetPageCategories($language: String = "en") {
    pageCategories( where: { language: $language, parent: 0 } ) {
      nodes {
        databaseId
        name
        slug
      }
    }
  }
`;

SearchPage.queries = [
  {
    query: PageQuery,
    variables: getBasicQueryVars,
  },
  {
    query: PageCategoryQuery,
    variables: ({ uri }) => ({ language: getUrlLocale(uri) }),
  }
]

export default SearchPage;
